import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { formatCurrency, formatCompactCurrency, formatNumber } from '../../services/adminAnalyticsApi';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  return (
    <div className="ab-card" style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem' }}>
      <p style={{ fontWeight: 600, margin: '0 0 0.25rem' }}>{label}</p>
      {row?.count ? (
        <p style={{ fontSize: '0.75rem', color: 'var(--ab-muted-fg)', margin: '0 0 0.25rem' }}>
          {formatNumber(row.count)} policies
        </p>
      ) : null}
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color, margin: '0.125rem 0', display: 'flex', gap: '0.5rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: entry.color, marginTop: 5 }} />
          {entry.name}: {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
};

function normalizeTrendData(trendData) {
  return (trendData || []).map((d) => {
    const month = d.month || '';
    const label = month.length >= 7
      ? new Date(`${month.substring(0, 7)}-01`).toLocaleDateString('en-KE', { month: 'short', year: 'numeric' })
      : month;
    return {
      ...d,
      premium: Number(d.premium) || 0,
      commission: Number(d.commission) || 0,
      count: Number(d.policies ?? d.count) || 0,
      label,
    };
  });
}

const RevenueTrendChart = ({ trendData = [] }) => {
  const data = normalizeTrendData(trendData);
  const totalPremium = data.reduce((s, i) => s + i.premium, 0);
  const totalCommission = data.reduce((s, i) => s + i.commission, 0);
  const rangeLabel = data.length > 1
    ? `${data[0].label} - ${data[data.length - 1].label}`
    : data[0]?.label || 'No data';

  return (
    <div className="ab-card">
      <div className="ab-card-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%', gap: '0.75rem' }}>
          <div>
            <h3 className="ab-card-title">Revenue &amp; Commission Trends</h3>
            <p className="ab-page-subtitle">Paid premium by month (Africa/Nairobi) from {rangeLabel}</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span className="ab-refresh-badge">Premium {formatCompactCurrency(totalPremium)}</span>
            <span className="ab-badge warning">Commission {formatCompactCurrency(totalCommission)}</span>
          </div>
        </div>
      </div>
      <div className="ab-card-body">
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="premiumGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1a2f5c" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#1a2f5c" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="commGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f5c518" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f5c518" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} />
              <YAxis
                yAxisId="premium"
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatCompactCurrency}
              />
              <YAxis
                yAxisId="commission"
                orientation="right"
                tick={{ fontSize: 11, fill: '#b45309' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatCompactCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area yAxisId="premium" type="monotone" dataKey="premium" name="Premium" stroke="#1a2f5c" fill="url(#premiumGrad)" strokeWidth={2.5} />
              <Area yAxisId="commission" type="monotone" dataKey="commission" name="Commission" stroke="#f5c518" fill="url(#commGrad)" strokeWidth={2.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default RevenueTrendChart;
